import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteContext } from '@/contexts/SiteContext';

interface SMOCROAccessResult {
  isSMOCROUser: boolean;
  organizationId: string | null;
  organizationName: string | null;
  organizationType: 'smo' | 'cro' | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to check if current user belongs to an SMO or CRO organization
 * PRIORITY 1: If on SMO/CRO subdomain (site.type === 'cro'), user has access
 * PRIORITY 2: Falls back to checking organization type from user profile
 * 
 * Supports both 'smo' and 'cro' organization types
 */
export function useSMOCROAccess(): SMOCROAccessResult {
  const { user, userProfile } = useAuth();
  const { site, isLoading: siteLoading } = useSiteContext();

  const { data, isLoading, error } = useQuery({
    queryKey: ['smo-cro-access', userProfile?.organization_id, site?.id, site?.type],
    queryFn: async () => {
      // PRIORITY 1: If on CRO/SMO subdomain, use site context directly
      if (site?.type === 'cro') {
        return {
          isSMOCROUser: true,
          organizationId: site.id,
          organizationName: site.display_name,
          organizationType: 'cro' as const,
        };
      }

      // PRIORITY 2: Fall back to profile organization check
      if (userProfile?.organization_id) {
        const { data: org, error: orgError } = await supabase
          .from('organizations')
          .select('id, name, type')
          .eq('id', userProfile.organization_id)
          .maybeSingle();

        if (orgError) throw orgError;

        const isSMOorCRO = org?.type === 'cro' || org?.type === 'smo';
        if (isSMOorCRO) {
          return {
            isSMOCROUser: true,
            organizationId: org!.id,
            organizationName: org!.name,
            organizationType: org!.type as 'smo' | 'cro',
          };
        }
      }

      // PRIORITY 3: Check organization_members for any SMO/CRO membership
      if (user?.id) {
        const { data: memberships, error: memError } = await supabase
          .from('organization_members')
          .select('organization_id, organizations!inner(id, name, type)')
          .eq('user_id', user.id);

        if (memError) throw memError;

        const membership = memberships?.find(
          (m: any) => m.organizations?.type === 'cro' || m.organizations?.type === 'smo'
        );

        if (membership) {
          return {
            isSMOCROUser: true,
            organizationId: (membership.organizations as any).id,
            organizationName: (membership.organizations as any).name,
            organizationType: (membership.organizations as any).type as 'smo' | 'cro',
          };
        }
      }

      return { isSMOCROUser: false, organizationId: null, organizationName: null, organizationType: null };
    },
    enabled: !!user && !siteLoading,
    staleTime: 5 * 60 * 1000,
  });

  return {
    isSMOCROUser: data?.isSMOCROUser ?? false,
    organizationId: data?.organizationId ?? null,
    organizationName: data?.organizationName ?? null,
    organizationType: data?.organizationType ?? null,
    isLoading: isLoading || siteLoading,
    error: error as Error | null,
  };
}

// Re-export for backwards compatibility
export { useSMOCROAccess as useCROAccess };
